import { lazy, ReactNode, Suspense, useEffect, useRef } from 'react'

import { useDecision } from '@optimizely/react-sdk'
import {
  NavigationType,
  useLocation,
  useNavigationType,
} from 'react-router-dom'

import { useStaticIntl } from '@tribeplatform/react-components/i18n'
import { useAuthToken } from '@tribeplatform/react-sdk/hooks'
import { SlateKitProvider } from '@tribeplatform/react-slate-kit/components'
import { useSlateKit } from '@tribeplatform/react-slate-kit/hooks'

import type { AppInitialProps } from '../@types/index.js'
import { getAssetPath } from '../configs/static.js'
import { Wrapper } from './components/Admin/Editor/SlateWrappers/index.js'
import { CookieContextProvider } from './components/Apps/CookieConsent/CookieContext.js'
import { AccountVerificationAlert } from './components/Auth/AccountVerificationAlert.js'
import { RenderError } from './components/Error/Error.js'
import { ErrorFallback } from './components/Error/ErrorBoundry.js'
import { InteractionComponents } from './interactions/InteractionComponents.js'
import { MobileSidebarProvider } from './MobileSidebarProvider.js'
import { FeaturesList } from './providers/optimizely/feature.list.js'
import { getRegisteredBlocks } from './registered-blocks.js'

function ScrollToTop() {
  const { pathname } = useLocation()
  const navigationType = useNavigationType()
  const prevPathname = useRef(pathname)

  useEffect(() => {
    if (
      navigationType !== NavigationType.Pop &&
      pathname !== prevPathname.current
    ) {
      window.scrollTo(0, 0)
    }
    prevPathname.current = pathname
  }, [navigationType, pathname])

  return null
}

const DesktopNotification = lazy(() =>
  import('./components/DesktopNotification.js').then(m => ({
    default: m.DesktopNotification,
  })),
)
const CookieConsent = lazy(() =>
  import('./components/Apps/CookieConsent/CookieConsent.js').then(m => ({
    default: m.CookieConsent,
  })),
)

export const SlateApp = ({
  pageProps,
  children,
}: {
  pageProps: AppInitialProps
  children: ReactNode
}) => {
  const registeredBlocks = useStaticIntl(getRegisteredBlocks)
  const [decisionDynamicBlockSettings] = useDecision(
    FeaturesList.DynamicBlockSettings,
  )
  const slateKit = useSlateKit({
    blocks: registeredBlocks,
    blockWrapper: Wrapper,
    dynamicBlockSettings: decisionDynamicBlockSettings.enabled,
  })

  const { data: { network, member } = pageProps.authToken } = useAuthToken({
    useQueryOptions: {
      // this is here to prevent the whole page from re-rendering
      notifyOnChangeProps: ['data'],
    },
  })

  return (
    <SlateKitProvider
      kit={slateKit}
      context={{
        network,
        actorId: member?.id,
        space: null,
        networkId: network?.id,
        memberBannerPlaceholders: [
          getAssetPath('images/banners/banner-1.jpg'),
          getAssetPath('images/banners/banner-2.jpg'),
          getAssetPath('images/banners/banner-3.jpg'),
          getAssetPath('images/banners/banner-4.jpg'),
          getAssetPath('images/banners/banner-5.jpg'),
        ],
      }}
      onError={({ error, ...extras }) => {
        const slateError = new RenderError({
          message: extras,
          error,
          name: 'SlateError',
        })
        logger.error(slateError)
      }}
      ErrorFallback={ErrorFallback}
    >
      <MobileSidebarProvider navigationSlates={network?.navigationSlates}>
        <ScrollToTop />
        <InteractionComponents />
        <Suspense fallback={<></>}>
          <DesktopNotification />
        </Suspense>
        <AccountVerificationAlert />
        {children}
        <Suspense fallback={<></>}>
          <CookieContextProvider>
            <CookieConsent />
          </CookieContextProvider>
        </Suspense>
      </MobileSidebarProvider>
    </SlateKitProvider>
  )
}
